import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight } from 'lucide-react';
import { FaLeaf, FaSeedling, FaSync, FaUserTie, FaShieldAlt, FaHeartbeat, FaClipboardList, FaTruck, FaCheckCircle } from 'react-icons/fa';
import { MdLocalFlorist } from 'react-icons/md';
import './ServiceDetail.css';

const ACCENT = '#2e7d32';
const ACCENT_LIGHT = '#66bb6a';
const ACCENT_RGB = '46,125,50';

const benefits = [
  {
    emoji: <FaLeaf size={22} />,
    title: 'Premium Plant Collection',
    desc: 'Access indoor, outdoor, flowering, herbal, and decorative plants selected by experts.'
  },

  {
    emoji: <MdLocalFlorist size={22} />,
    title: 'Designer Pot Styling',
    desc: 'Beautiful decorative pots professionally matched to your interior and landscape theme.'
  },

  {
    emoji: <FaSync size={22} />,
    title: 'Seasonal Plant Exchange',
    desc: 'Fresh plants and new themes every few months to keep your space vibrant.'
  },

  {
    emoji: <FaUserTie size={22} />,
    title: 'Professional Maintenance',
    desc: 'Weekly visits including cleaning, pruning, nutrition, and plant health monitoring.'
  },

  {
    emoji: <FaShieldAlt size={22} />,
    title: 'Plant Replacement Guarantee',
    desc: 'Dead or unhealthy plants are replaced without additional purchase cost.'
  },

  {
    emoji: <FaHeartbeat size={22} />,
    title: 'Wellness & Air Quality',
    desc: 'Improve indoor wellness, air purification, and overall environmental quality naturally.'
  }
];

const steps = [
  {
    num: '01',
    icon: <FaClipboardList size={20} />,
    title: 'Requirement Analysis',
    desc: 'We study your space, lighting, theme, and plant requirements.'
  },

  {
    num: '02',
    icon: <MdLocalFlorist size={20} />,
    title: 'Plant Selection',
    desc: 'Choose suitable plants, decorative pots, and styling options.'
  },

  {
    num: '03',
    icon: <FaTruck size={20} />,
    title: 'Installation',
    desc: 'Our team completes professional setup and placement.'
  },

  {
    num: '04',
    icon: <FaLeaf size={20} />,
    title: 'Managed Maintenance',
    desc: 'Enjoy regular visits, plant care, replacement, and seasonal refresh.'
  }
];

const stats = [
  { val: '500+', label: 'Plant Species' },
  { val: '50+', label: 'Rare Varieties' },
  { val: '1K+', label: 'Happy Gardeners' },
  { val: '100%', label: 'Organic Sourced' },
];

export default function PlantLibraryPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Plant Library — Nisarg Nursery';
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.sdp-observe').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="sdp-wrapper"
      style={{ '--accent': ACCENT, '--accent-light': ACCENT_LIGHT, '--accent-dark': '#1b5e20', '--accent-rgb': ACCENT_RGB }}
    >
      {/* ════ HERO ════ */}
      <section className="sdp-hero">
        <div className="sdp-hero-bg">
          <img src="/images/Spider Plant.jpg" alt="Plant Library" className="sdp-hero-img" />
          <div className="sdp-hero-overlay" />
        </div>
        <div className="sdp-hero-content">
          <nav className="sdp-breadcrumb">
            <Link to="/">Home</Link> › <Link to="/services">Services</Link> › <span>Plant Library</span>
          </nav>
          <div className="sdp-service-tag">
            <BookOpen size={16} /> <FaLeaf /> Managed Green Ecosystem Service
          </div>
          <span className="sdp-hero-icon"><BookOpen size={56} strokeWidth={1.5} /></span>
          <h1 className="sdp-hero-title">
            Your Personal <span className="accent-word">Plant Library</span> &amp; Experience
          </h1>
          <p className="sdp-hero-desc">
            Transform your home, office, hospital, cafe, or commercial space with professionally managed plants, decorative pots, seasonal plant exchange, and year-round maintenance support.
          </p>
          <div className="sdp-hero-cta">
            <Link to="/contact" className="sdp-btn-primary">
              Get in Touch <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="sdp-btn-outline">← Back to Services</Link>
          </div>
        </div>
        <div className="sdp-hero-scroll">
          <span>SCROLL</span>
          <div className="sdp-scroll-line" />
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="sdp-stats">
        {stats.map(s => (
          <div key={s.label} className="sdp-stat sdp-observe">
            <span className="sdp-stat-val">{s.val}</span>
            <span className="sdp-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      <section className="sdp-section sdp-overview">
        <div className="sdp-section-inner">
          <div className="sdp-overview-grid">
            <div className="sdp-overview-left sdp-observe">
              <div className="sdp-overview-img-wrap">
                <img src="/images/monstera.png" alt="Plant Library" className="sdp-overview-img" />
                <div className="sdp-overview-badge">
                  <div className="sdp-overview-badge-dot" />
                  Always Updated Catalog
                </div>
              </div>
            </div>
            <div className="sdp-overview-right sdp-observe">
              <div className="sdp-section-tag"><FaLeaf /> Complete Green Management</div>
              <h2 className="sdp-section-title">Green Spaces Without<br />The Maintenance Hassle</h2>
              <div className="sdp-overview-text">
                <p>A Plant Library is a smart and convenient way to enjoy the beauty of nature without worrying about plant care and maintenance. At Nisarg Plant Library, we provide carefully selected indoor plants, outdoor plants, flowering plants, air-purifying plants, and decorative pots that are professionally installed and managed by our expert team. It is an ideal solution for homes, offices, hospitals, cafés, hotels, showrooms, and commercial spaces looking to create a refreshing green environment.</p>
                <p>With our managed plant care service, you don't have to worry about watering, maintenance, plant health, replacements, or seasonal updates. Our team handles everything, including regular maintenance, plant replacement, and seasonal plant exchange, ensuring your space remains fresh, vibrant, and visually appealing throughout the year. Nisarg Plant Library helps improve air quality, enhance wellness, and create beautiful green spaces with complete peace of mind.  </p>
              </div>
              <div className="sdp-features-list">
                {[
                  'Premium Plants',
                  'Designer Pots',
                  'Weekly Maintenance',
                  'Plant Replacement',
                  'Seasonal Exchange',
                  'Professional Installation'
                ].map(f => (
                  <div key={f} className="sdp-feature-item">
                    <div className="sdp-feature-icon"><FaCheckCircle /></div>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="sdp-section sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaCheckCircle /> Why Choose Us</div>
          <h2 className="sdp-section-title">Why Choose Our <br />Plant Library Service</h2>
          <div className="sdp-benefits-grid">
            {benefits.map(b => (
              <div key={b.title} className="sdp-benefit-card sdp-observe">
                <span className="sdp-benefit-emoji">{b.emoji}</span>
                <h3 className="sdp-benefit-title">{b.title}</h3>
                <p className="sdp-benefit-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="sdp-section sdp-process sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaSync /> How Plant Library Works</div>
          <h2 className="sdp-section-title">Start Your Green Journey</h2>
          <div className="sdp-process-steps">
            {steps.map(s => (
              <div key={s.num} className="sdp-step sdp-observe">
                <div className="sdp-step-num">{s.num}</div>
                <div className="sdp-step-icon">{s.icon}</div>
                <h3 className="sdp-step-title">{s.title}</h3>
                <p className="sdp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="sdp-cta sdp-observe">
        <span className="sdp-cta-icon"><FaSeedling size={28} /></span>
        <h2 className="sdp-cta-title">Ready For A Professionally Managed Green Space?</h2>
        <p className="sdp-cta-desc">Let Nisarg Plant Library handle plant selection, installation, maintenance, and seasonal refresh while you enjoy a greener, healthier environment.</p>
        <div className="sdp-cta-btns">
          <Link to="/contact" className="sdp-btn-primary">
            Contact Us
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </Link>
          <Link to="/services" className="sdp-btn-outline">← All Services</Link>
        </div>
      </div>
    </div>
  );
}
